import { useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, SearchBar, Pagination, EmptyState, StatusBadge, useList, formatDate, tableClass, theadClass, thClass, trClass, tdClass, tdFirstClass } from "@/components/shared";
import RightDrawer from "@/components/RightDrawer";
import { MessagesSquare } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AdminMessages() {
  const { data, pagination, loading, page, setPage, q, setQ, filters, setFilters } = useList("/admin/messages");
  const [selected, setSelected] = useState(null);
  const [tracking, setTracking] = useState([]);

  const open = async (m) => {
    setSelected(m);
    setTracking([]);
    const res = await api.get(`/admin/messages/${m.id}/tracking`);
    setTracking(res.data || []);
  };

  return (
    <div>
      <PageHeader title="Messages" subtitle="Semua pesan lintas user untuk kebutuhan support." />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <SearchBar value={q} onChange={setQ} placeholder="Cari kontak, nomor..." testid="admin-message-search" />
        <Select value={filters.direction || "all"} onValueChange={(v) => setFilters({ ...filters, direction: v === "all" ? "" : v })}>
          <SelectTrigger className="w-40 bg-white" data-testid="admin-message-direction-filter"><SelectValue placeholder="Arah" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua arah</SelectItem><SelectItem value="inbound">Inbound</SelectItem><SelectItem value="outbound">Outbound</SelectItem></SelectContent>
        </Select>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-40 bg-white" data-testid="admin-message-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua status</SelectItem>{["pending", "sent", "delivered", "read", "failed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!loading && data.length === 0 ? (
        <EmptyState icon={MessagesSquare} title="Belum ada pesan" description="Pesan dari semua user akan muncul di sini." />
      ) : (
        <div className="bg-white rounded-lg overflow-hidden">
          <table className={tableClass}>
            <thead className={theadClass}>
              <tr><th className={thClass}>Kontak</th><th className={`${thClass} hidden md:table-cell`}>User</th><th className={`${thClass} hidden lg:table-cell`}>Arah</th><th className={thClass}>Status</th><th className={`${thClass} hidden md:table-cell`}>Waktu</th></tr>
            </thead>
            <tbody>
              {data.map((m) => (
                <tr key={m.id} onClick={() => open(m)} className={trClass} data-testid={`admin-message-row-${m.id}`}>
                  <td className={tdFirstClass}>
                    <p className="font-medium text-[#111111]">{m.contact_name || m.to_number}</p>
                    <p className="text-xs text-[#767676] truncate max-w-[220px]">{m.content?.text || `[${m.message_type}]`}</p>
                  </td>
                  <td className={`${tdClass} hidden md:table-cell`}>{m.user?.name || "-"}</td>
                  <td className={`${tdClass} hidden lg:table-cell capitalize`}>{m.direction}</td>
                  <td className={tdClass}><StatusBadge status={m.status} /></td>
                  <td className={`${tdClass} hidden md:table-cell text-[#767676] text-xs`}>{formatDate(m.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination pagination={pagination} onPage={setPage} />

      <RightDrawer open={!!selected} onClose={() => setSelected(null)} title="Detail Pesan (Support)" description="Tracking internal telah disanitasi." testid="admin-message-drawer">
        {selected && (
          <div className="space-y-4">
            <div className="flex items-center gap-3"><StatusBadge status={selected.status} /><span className="text-sm text-neutral-500">{formatDate(selected.created_at)}</span></div>
            <div className="rounded-lg bg-[#FFF3C2] p-4">
              <p className="text-sm text-neutral-800 whitespace-pre-wrap">{selected.content?.text || `[${selected.message_type}]`}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-xs text-neutral-500">User</p><p className="font-medium">{selected.user?.name}</p></div>
              <div><p className="text-xs text-neutral-500">Channel</p><p className="font-medium">{selected.channel_name}</p></div>
              <div><p className="text-xs text-neutral-500">Dari</p><p className="font-medium">{selected.from_number}</p></div>
              <div><p className="text-xs text-neutral-500">Ke</p><p className="font-medium">{selected.to_number}</p></div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">Audit Tracking (disanitasi)</p>
              <div className="space-y-2">
                {tracking.map((t, i) => (
                  <div key={i} className="flex items-center justify-between text-sm bg-[#F5F5F5] rounded-[4px] px-3 py-2" data-testid={`tracking-row-${i}`}>
                    <span className={t.success ? "text-[#0A8A00]" : "text-[#CC0008]"}>{t.success ? "Sukses" : "Gagal"}</span>
                    <span className="text-xs text-neutral-500">{formatDate(t.created_at)}</span>
                  </div>
                ))}
                {tracking.length === 0 && <p className="text-xs text-neutral-400">Memuat…</p>}
              </div>
            </div>
          </div>
        )}
      </RightDrawer>
    </div>
  );
}
